import { useState, useEffect, useLayoutEffect, Fragment, useCallback, useRef } from 'react';
import { Typography } from '../../../../core/components/molecules/Typography';
import { Paper, Lines, LineContent, ContentWrapper } from './variants';
import { Input } from '../../../../core/components/atoms/Input';
import { LoadingIcon } from 'morpheus-ui';
import { useBoardConfig } from '../../../hooks/useBoardConfig';
import { useEssayCoreStore } from '../../../stores/essayCoreStore';

interface IEssayPaper {
  correction: number;
  fontSize: 'small' | 'default' | 'large' | 'verySmall';
  essayText: string;
  essayTitle?: string;
  blocked: boolean;
  loading: boolean;
  setText: (text: string) => void;
  setTitle?: (title: string) => void;
  setIsValid?: (isValid: boolean) => void;
  mobile?: boolean;
}

export default function EssayPaper({
  correction,
  fontSize,
  setText,
  essayText,
  essayTitle,
  setTitle,
  blocked = true,
  loading,
  setIsValid,
  mobile = false,
}: IEssayPaper) {
  const paperRef = useRef<HTMLTextAreaElement>(null);

  const { board } = useBoardConfig(Number(correction) || 2);
  const { setText: setCoreText, setTitle: setCoreTitle } = useEssayCoreStore();

  const [paperLines, setPaperLines] = useState<number>(0);
  const [paperHeight, setPaperHeight] = useState<number>(900);

  const sizes = {
    default: 16,
    small: 14,
    large: 18,
    verySmall: 12
  };

  const spaces = {
    default: 2,
    small: 1.5,
    verySmall: 1.2,
    large: 2,
  };

  useEffect(() => {
    const LINE_HEIGHT = 30;
    const finalLines = paperLines <= board.lines ? board.paperHeight + 20 : paperLines * LINE_HEIGHT + 20;
    setPaperHeight(paperLines > 30 ? finalLines + 15 : finalLines);
  }, [paperLines, board]);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if(setText) {
      setText(e.target.value);
      setCoreText(e.target.value);
    }
  }, [setText, setCoreText]);

  const sanitizePastedText = (text: string) =>
    text
      .replace(/\r\n?/g, '\n')
      .replace(/[^\S\n]+/g, ' ')
      .replace(/ *\n */g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!setText) {
      return;
    }

    e.preventDefault();

    const textarea = paperRef.current;
    if (!textarea) {
      return;
    }

    const pasted = sanitizePastedText(e.clipboardData.getData('text/plain'));
    const current = essayText || '';
    const start = textarea.selectionStart ?? current.length;
    const end = textarea.selectionEnd ?? current.length;

    const newValue = current.slice(0, start) + pasted + current.slice(end);

    setText(newValue);
    setCoreText(newValue);

    requestAnimationFrame(() => {
      const pos = start + pasted.length;
      textarea.setSelectionRange(pos, pos);
    });
  }, [essayText, setText, setCoreText]);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if(setTitle) {
      setTitle(e.target.value);
      setCoreTitle(e.target.value);
    }
  }, [setTitle, setCoreTitle]);

  // The line count is read from the visible textarea itself, so it always matches
  // how the browser really wraps the text (zoom, DPI scaling, font hinting).
  const countLines = useCallback(() => {
    const textarea = paperRef.current;

    if (!textarea) {
      return 0;
    }

    const styles = getComputedStyle(textarea);
    const lineHeight = parseFloat(styles.lineHeight) || 30;
    const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    const previousHeight = textarea.style.height;

    // Collapse the textarea so scrollHeight reflects only the content.
    textarea.style.height = 'auto';
    const contentHeight = textarea.scrollHeight - padding;
    textarea.style.height = previousHeight;

    return Math.max(1, Math.round(contentHeight / lineHeight));
  }, []);

  const updateLines = useCallback(() => {
    const measured = countLines();
    const lines = Math.max(measured, board.lines);

    if (setIsValid) {
      // Mobile has no line limit
      setIsValid(mobile || measured <= board.lines);
    }

    setPaperLines(lines);
  }, [countLines, board.lines, mobile, setIsValid]);

  const hasPaper = paperLines > 0;

  useLayoutEffect(() => {
    updateLines();
  }, [essayText, fontSize, hasPaper, updateLines]);

  useEffect(() => {
    const textarea = paperRef.current;

    if (!textarea || typeof ResizeObserver === 'undefined') {
      return;
    }

    let lastWidth = textarea.clientWidth;
    const observer = new ResizeObserver(() => {
      if (textarea.clientWidth !== lastWidth) {
        lastWidth = textarea.clientWidth;
        updateLines();
      }
    });

    observer.observe(textarea);

    return () => observer.disconnect();
  }, [hasPaper, updateLines]);

  useEffect(() => {
    // Wrapping changes once the web font finishes loading.
    document.fonts?.ready.then(updateLines);
  }, [updateLines]);

  return (
    <Fragment>
      {board.title && (
        <div className='m-auto mt-8 mb-4 xs:w-full lg:w-[750px]'>
          <Input
            placeholder='Titulo'
            value={essayTitle}
            onChange={handleTitleChange}
            variant='sm'
          />
        </div>
      )}
      <div>
        <div className='m-auto mb-1 h-5 xs:w-full lg:w-[750px]'>
          {loading ? (
            <div className='flex w-full items-center justify-end'>
              <Typography size='mini' variant='italic' className='text-right animate-pulse'>
                Salvando...
              </Typography>
            </div>
          ) : (
            <Typography size='mini' variant='italic' className='text-right'>
              {essayText !== null || essayText !== "" ? 'Redação salva' : ''}
            </Typography>
          )}
        </div>
        {paperLines === 0 ? (
          <div className='flex w-full items-center justify-center'>
            <LoadingIcon className='stroke-current h-32 animate-spin fill-none text-center' />
          </div>
        ) : (
          <div className={Paper()} style={{ height: paperHeight }}>
            <div className='absolute z-0 h-full w-full'>
              {[...Array(paperLines)].map((__, i) => (
                <div className={Lines()} key={i}>
                  <div className={LineContent()}>
                    <div className='h-4 w-4 rounded-pill bg-color-lighter' />
                    <Typography
                      size='default'
                      className={
                        mobile || i + 1 <= board.lines
                          ? 'text-mono-color-medium'
                          : 'text-red-500'
                      }
                    >
                      {i + 1}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>

            <div className={ContentWrapper()}>
              <textarea
                ref={paperRef}
                value={essayText || ''}
                style={{
                  wordSpacing: spaces[fontSize],
                  fontSize: sizes[fontSize],
                  height: paperHeight
                }}
                disabled={blocked}
                onChange={handleTextChange}
                onPaste={handlePaste}
                className='inline-flex w-full resize-none items-center justify-center overflow-hidden border-color-transparent bg-color-transparent px-3 py-1 text-left font-notoSans font-normal leading-[30px] focus:border-color-transparent focus:ring-0'
                placeholder='Escreva aqui a sua redação'
                rows={1}
                spellCheck={false}
                autoCorrect='off'
                autoCapitalize='off'
              />
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
}
